import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChatImageDropZone,
  useChatImages,
} from "@/core/components/chat-images";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { AppAside } from "@/app-shell";
import { LiveAssistantMessage } from "@/payroll/ai-script/ai-script-explanation-chat/live-assistant-message";
import {
  AGENT_IMAGE_HINT,
  TemplateChatComposer,
} from "@/profitability/template-chat/template-chat-composer";
import { TemplateChatMessageView } from "@/profitability/template-chat/template-chat-message";
import type { useTemplateChatSession } from "@/profitability/template-chat/use-template-chat";
import type { ProfitabilityReport } from "@/profitability/types";
import type { EditAgentState, RuleEditContext } from "./use-agent-nav";

/**
 * Edit agent: the conversation an agent is set up in and changed through,
 * beside its page. Volca builds a draft from it; the Setup tab shows the
 * draft and publishes it. A message asked from elsewhere (Settings → Ask for
 * a check) is sent once the conversation has loaded, and a suggestion from
 * an empty state waits in the composer; the address forgets both after.
 */
export const EditAgentAside = ({
  businessId,
  report,
  session,
  open,
  onClose,
  onReview,
}: {
  businessId: string;
  report: ProfitabilityReport;
  session: ReturnType<typeof useTemplateChatSession>;
  open: boolean;
  onClose: () => void;
  /**
   * Opens the newest build's draft for review; absent while the agent is
   * being set up, when the page already is its sample week.
   */
  onReview?: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as EditAgentState | null) ?? null;
  // Only what Edit agent was opened with; the page's other state (Ask
  // Volca's question) is not its to send or to clear.
  const carried =
    state?.request || state?.draft || state?.ruleContext ? state : null;
  const [ruleContext, setRuleContext] = useState<RuleEditContext | null>(null);
  const draftState = useState("");
  const [, setDraft] = draftState;
  const documentState = useState<string[]>([]);
  const imageState = useState<File[]>([]);
  const chatImages = useChatImages(imageState, {
    refusedHint: AGENT_IMAGE_HINT,
  });
  const endRef = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => heading.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);
  const sentAsk = useRef(false);
  const { chat, answering, sendMessage, messages } = session;
  const building = report.build_status === "building";
  // The newest build's message offers to open what it built, once there is a draft to review.
  const lastBuild =
    report.draft_sample_run_id && report.build_status === "built"
      ? messages.findLast((message) => message.kind === "build_update")
      : undefined;

  useEffect(() => {
    if (!open || !carried) return;
    if (carried.draft) setDraft(carried.draft);
    if (carried.ruleContext) setRuleContext(carried.ruleContext);
    if (carried.request) {
      if (sentAsk.current || !chat.isSuccess || answering) return;
      sentAsk.current = true;
      void sendMessage(carried.request, [], []);
    }
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    open,
    carried,
    chat.isSuccess,
    answering,
    sendMessage,
    setDraft,
    navigate,
    location.pathname,
    location.search,
  ]);

  // The "building" line is the last thing in the conversation: bring it into
  // view when it appears, or the reply that started the build looks like the end.
  // The same for a send error, or it renders out of view.
  // Again when the aside reopens: while closed it stays mounted but hidden, and
  // its slot shows only on the render after this one, so the scroll waits a
  // frame for a box to move.
  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      endRef.current?.scrollIntoView({ block: "end" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [
    messages.length,
    session.liveText,
    session.steps.length,
    building,
    open,
    session.error,
  ]);

  return (
    <AppAside label="Edit agent" open={open} keepMounted onClose={onClose}>
      <ChatImageDropZone
        onFiles={chatImages.addFiles}
        className="h-full min-h-0 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11"
      >
        <Stack className="h-full min-h-0" gap={0}>
          <Stack
            direction="row"
            align="center"
            justify="between"
            gap={3}
            className="border-b border-line p-5"
          >
            <Text ref={heading} tabIndex={-1} variant="title" render={<h2 />}>
              Edit agent
            </Text>
            <Stack direction="row" align="center" gap={1}>
              {session.summarizable && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={answering}
                  title="Volca reads a summary of the conversation so far instead of every message. Long conversations stay quicker and more focused."
                  onClick={session.summarize}
                >
                  Summarize
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close Edit agent"
                onClick={onClose}
              >
                <XIcon aria-hidden />
              </Button>
            </Stack>
          </Stack>
          <Stack
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5"
            gap={4}
          >
            {chat.isPending && (
              <Text role="status" tone="muted">
                Loading the conversation…
              </Text>
            )}
            {chat.isError && (
              <Stack gap={3} align="start">
                <Text tone="destructive" role="alert">
                  The conversation could not load.
                </Text>
                <Button variant="secondary" onClick={() => void chat.refetch()}>
                  Try again
                </Button>
              </Stack>
            )}
            {chat.isSuccess && messages.length === 0 && !answering && (
              <Text tone="muted">
                {report.published
                  ? "Tell Volca what to change: a check to add, a figure to break down, a file to read. It builds a draft for you to review before anything changes."
                  : report.draft_sample_run_id
                    ? "Your sample week is beside this. Tell Volca what to change: a figure, a check, a file it should read. It rebuilds the sample for you to check."
                    : "Describe the report you need and attach a week of example files. Volca builds a draft for you to review and publish."}
              </Text>
            )}
            {messages.map((message, index) => (
              <TemplateChatMessageView
                key={message.id}
                businessId={businessId}
                reportId={report.id}
                message={message}
                first={index === 0}
                onReview={message.id === lastBuild?.id ? onReview : undefined}
              />
            ))}
            {answering && (
              <LiveAssistantMessage
                displayedLiveText={session.liveText}
                progressSteps={session.steps}
              />
            )}
            {building && !answering && (
              <Stack
                direction="row"
                align="center"
                gap={2}
                role="status"
                className="mt-2"
              >
                <Spinner size="sm" tone="secondary" aria-hidden />
                <Text tone="muted">
                  Building the agent. This takes a few minutes.
                </Text>
              </Stack>
            )}
            {session.error && (
              <Text tone="destructive" role="alert">
                {session.error} Try sending your message again.
              </Text>
            )}
            <div ref={endRef} />
          </Stack>
          {ruleContext && (
            <Stack
              gap={1}
              className="mx-5 mb-3 rounded-md border border-line p-3"
            >
              <Stack direction="row" gap={2} justify="between" align="start">
                <Text
                  variant="label"
                  className="min-w-0 [overflow-wrap:anywhere]"
                >
                  Editing {ruleContext.label}
                </Text>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remove rule context"
                  disabled={answering}
                  onClick={() => setRuleContext(null)}
                >
                  <XIcon aria-hidden />
                </Button>
              </Stack>
              {ruleContext.example && (
                <Text variant="caption" tone="muted">
                  Example: {ruleContext.example.name} ·{" "}
                  {ruleContext.example.flag}
                </Text>
              )}
              <Text variant="caption" tone="muted">
                Describe the change. Review the draft and sample results before
                publishing.
              </Text>
              <Link
                to={ruleContext.href}
                className="inline-flex items-center text-sm underline underline-offset-4 pointer-coarse:min-h-11"
              >
                Back to rule
              </Link>
            </Stack>
          )}
          <TemplateChatComposer
            draftState={draftState}
            documentState={documentState}
            imageState={imageState}
            businessId={businessId}
            disabled={answering}
            placeholder={
              ruleContext
                ? "What should this rule do differently?"
                : "Reply, or drop a screenshot…"
            }
            onSend={async (message, documents, images) => {
              const example = ruleContext?.example;
              const context = ruleContext
                ? `Update only the rule “${ruleContext.label}” (rule id: ${ruleContext.id}).${example ? ` Use ${example.name} as an example (${example.dataset}, record ${example.recordId}): ${example.flag}` : ""}\n\n${message}`
                : message;
              const sent = await sendMessage(context, documents, images);
              if (sent) setRuleContext(null);
              return sent;
            }}
          />
        </Stack>
      </ChatImageDropZone>
    </AppAside>
  );
};
